import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Package, 
  MapPin, 
  ChevronDown, 
  User, 
  Clock, 
  Star,
  Heart,
  FileText,
  HelpCircle,
  CreditCard,
  LogOut,
  ArrowRight,
  Bike,
  Box,
  Zap,
  UtensilsCrossed,
  AlertTriangle
} from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { getBackendUrl } from "../utils/api";

const API_URL = getBackendUrl();

const FleetyGoHomePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showMyFleetyGo, setShowMyFleetyGo] = useState(false);
  const [pauseStatus, setPauseStatus] = useState({ orders_accepted: true, message: null, resume_at: null });

  useEffect(() => {
    const userData = localStorage.getItem("fleety_user");
    if (!userData) {
      navigate("/login");
      return;
    }
    setUser(JSON.parse(userData));
  }, [navigate]);

  // Fetch FleetyGo pause status
  useEffect(() => {
    const fetchPauseStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/fleetygo-status`);
        setPauseStatus(response.data);
      } catch (error) {
        console.error("Error fetching FleetyGo status:", error);
      }
    };

    fetchPauseStatus();
    
    // Poll every 2 minutes
    const interval = setInterval(fetchPauseStatus, 120000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("fleety_user");
    localStorage.removeItem("fleety_delivery_address");
    navigate("/");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="fleetygo-home">
      {/* Header */}
      <header className="bg-[#3FA9F5] sticky top-0 z-20">
        {/* Top Row */}
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="text-white" size={28} />
            <span className="text-white text-xl font-bold font-heading">FleetyGo</span>
          </div>
          
          {/* My FleetyGo Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowMyFleetyGo(!showMyFleetyGo)}
              className="flex items-center gap-1.5 text-white hover:text-white/90 transition-colors text-sm font-medium"
              data-testid="my-fleetygo-btn"
            >
              <span>My FleetyGo</span>
              <ChevronDown size={16} className={`transition-transform ${showMyFleetyGo ? "rotate-180" : ""}`} />
            </button>
            
            {showMyFleetyGo && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowMyFleetyGo(false)} />
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl z-40 overflow-hidden animate-fadeIn">
                  {/* User Info */}
                  <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-[#3FA9F5]/5 to-white">
                    <p className="font-semibold text-gray-900">{user.full_name}</p>
                    <p className="text-sm text-gray-500">{user.phone_number}</p>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-1">
                    {/* Switch to Fleety Food */}
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/home"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-orange-50 transition-colors text-left bg-gradient-to-r from-orange-50 to-white border-b border-orange-100">
                      <UtensilsCrossed size={18} className="text-orange-500" />
                      <span className="text-orange-600 font-semibold">Fleety Food</span>
                      <span className="ml-auto text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">Order Food</span>
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/fleetygo/profile"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                      <User size={18} className="text-[#3FA9F5]" />
                      <span className="text-gray-700">Profile</span>
                    </button>
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/fleetygo/orders"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                      <Clock size={18} className="text-[#3FA9F5]" />
                      <span className="text-gray-700">Past Deliveries</span>
                    </button>
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/fleetygo/favorites"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                      <Heart size={18} className="text-[#3FA9F5]" />
                      <span className="text-gray-700">Favorite Addresses</span>
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/fleetygo/about"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                      <FileText size={18} className="text-[#3FA9F5]" />
                      <span className="text-gray-700">About FleetyGo</span>
                    </button>
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/fleetygo/terms"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                      <FileText size={18} className="text-[#3FA9F5]" />
                      <span className="text-gray-700">Terms & Conditions</span>
                    </button>
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/fleetygo/faqs"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                      <HelpCircle size={18} className="text-[#3FA9F5]" />
                      <span className="text-gray-700">FAQs</span>
                    </button>
                    <button onClick={() => { setShowMyFleetyGo(false); navigate("/payment-methods"); }} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left">
                      <CreditCard size={18} className="text-[#3FA9F5]" />
                      <span className="text-gray-700">Payment Methods</span>
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button onClick={handleLogout} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-red-50 transition-colors text-left">
                      <LogOut size={18} className="text-red-500" />
                      <span className="text-red-500">Logout</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4">
        {/* FleetyGo Pause Banner - Purple */}
        {!pauseStatus.orders_accepted && (
          <div 
            className="bg-gradient-to-r from-purple-500 to-violet-500 rounded-xl p-4 shadow-lg mb-4"
            data-testid="fleetygo-pause-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl">📦</span>
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm sm:text-base leading-relaxed">
                  {pauseStatus.message || "FleetyGo is currently not accepting deliveries. We will be back soon!"}
                </p>
                <p className="text-white/80 text-xs sm:text-sm mt-1">
                  We will notify you when package deliveries resume.
                </p>
                {pauseStatus.resume_at && (
                  <p className="text-white/90 text-xs sm:text-sm mt-2 font-medium">
                    ⏰ Expected: {new Date(pauseStatus.resume_at).toLocaleString('en-IN', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                      day: 'numeric',
                      month: 'short'
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#3FA9F5] to-[#2196F3] rounded-2xl p-6 mb-6 text-white">
          <h1 className="text-2xl font-bold mb-2">Send Anything, Anywhere</h1>
          <p className="text-white/90 text-sm mb-4">Fast & reliable delivery up to 20 kg on bike</p>
          
          <Button
            onClick={() => navigate("/fleetygo/book")}
            className="bg-white text-[#3FA9F5] hover:bg-white/90 font-semibold px-6 py-3 rounded-xl shadow-lg"
            data-testid="send-package-btn"
          >
            <Package size={20} className="mr-2" />
            Send a Package
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>

        {/* Features */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="w-12 h-12 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <Bike className="text-[#3FA9F5]" size={24} />
            </div>
            <p className="text-xs font-medium text-gray-700">Bike Delivery</p>
            <p className="text-[10px] text-gray-500">Up to 20 kg</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="w-12 h-12 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <Zap className="text-[#3FA9F5]" size={24} />
            </div>
            <p className="text-xs font-medium text-gray-700">Fast Delivery</p>
            <p className="text-[10px] text-gray-500">Same day</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm">
            <div className="w-12 h-12 bg-[#3FA9F5]/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <MapPin className="text-[#3FA9F5]" size={24} />
            </div>
            <p className="text-xs font-medium text-gray-700">Live Tracking</p>
            <p className="text-[10px] text-gray-500">Real-time</p>
          </div>
        </div>

        {/* Pricing Info */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Simple Pricing</h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Delivery (per km)</span>
              <span className="font-semibold text-gray-900">₹10</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Minimum charge (0-3 km)</span>
              <span className="font-semibold text-gray-900">₹30</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Platform fee</span>
              <span className="font-semibold text-gray-900">₹20</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">GST (18%)</span>
              <span className="font-semibold text-gray-900">₹3.60</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Wait time (after 10 mins)</span>
              <span className="font-semibold text-gray-900">₹1/min</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">* First 10 minutes of wait time is free</p>
        </div>

        {/* What You Can Send */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">What You Can Send</h3>
          <div className="grid grid-cols-2 gap-2">
            {["Documents", "Food", "Medicine", "Books", "Keys", "Gifts"].map((item) => (
              <div key={item} className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 p-3 bg-red-50 rounded-lg">
            <p className="text-xs text-red-600 font-medium">⚠️ Not Allowed: Alcohol, Illegal items, Banned substances</p>
          </div>
        </div>

        {/* Back to Fleety */}
        <button
          onClick={() => navigate("/home")}
          className="w-full text-center text-[#3FA9F5] font-medium py-3"
        >
          ← Back to Fleety Food
        </button>
      </main>
    </div>
  );
};

export default FleetyGoHomePage;
